import { useAuth } from "@/lib/hooks/useAuth";
import { useEdition } from "@/lib/hooks/useEdition";
import { usePermission } from "@/lib/hooks/usePermission";
import { Edition, EditionJson } from "@/lib/types";
import { Burger, Button, Group, AppShell as MAppShell, Select, Stack } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { PropsWithChildren } from "react";
import { Link, NavLink } from "react-router-dom";
import { navRoutes } from "./nav/config";
import { NavRow } from "./nav/NavRow";
import { ProtectedLink } from "./nav/ProtectedLink";

export const AppShell = ({ children }: PropsWithChildren) => {
  const [opened, { toggle }] = useDisclosure();
  const { name, logout } = useAuth();
  const { edition, setEdition } = useEdition();

  const { data: editions } = useQuery<Edition[]>({
    queryKey: ["edition"],
    queryFn: async () => {
      const resp = await fetch("/api/edition");
      const data: EditionJson[] = await resp.json();
      return data.map(e => ({ id: e.id, start: new Date(e.start), end: new Date(e.end) }))
    },
    refetchOnWindowFocus: false
  });

  const { isAllowed } = usePermission("editions:switch")

  const handleEditionChange = (editionId: string | null) => {
    if (!editionId) return

    const newEdition = editions?.find(e => e.id === Number(editionId))
    if (!newEdition) return

    setEdition(newEdition)
  }

  return (
    <MAppShell
      header={{ height: 60 }}
      navbar={{ width: 300, breakpoint: "sm", collapsed: { desktop: true, mobile: !opened } }}
      padding="md"
    >
      <MAppShell.Header>
        <Group justify="space-between" h="100%" px="md">
          <Group h="100%" px="md">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Link to="/">
              <img src="/images/logo.png" className="h-8 object-contain pr-2" />
            </Link>
            <NavRow />
          </Group>
          <Group>
            {isAllowed &&
              <Select
                data={editions?.sort((a, b) => b.start.getTime() - a.start.getTime()).map(e => ({
                  value: e.id.toString(),
                  label: e.start.getFullYear().toString(),
                })) ?? []}
                value={edition?.id.toString() ?? null}
                onChange={(value) => handleEditionChange(value)}
                allowDeselect={false}
                searchable={false}
                maw={100}
                disabled={!edition}
              />
            }
            <Button variant="subtle" onClick={logout}>
              {name}
            </Button>
          </Group>
        </Group>
      </MAppShell.Header>

      <MAppShell.Navbar p="md">
        <Stack align="stretch" justify="center">
          {navRoutes.map((route) => {
            return route.permission ? (
              <ProtectedLink
                key={route.href.toString()}
                permission={route.permission}
                to={route.href}
              >
                {({ isActive }) => (
                  <Button
                    variant={isActive ? "light" : "subtle"}
                    className="text-gray-800 hover:text-red-500"
                  >
                    {route.title}
                  </Button>
                )}
              </ProtectedLink>
            ) : (
              <NavLink key={route.href.toString()} to={route.href}>
                {({ isActive }) => (
                  <Button
                    variant={isActive ? "light" : "subtle"}
                    className="text-gray-800 hover:text-red-500"
                  >
                    {route.title}
                  </Button>
                )}
              </NavLink>
            );
          })}
        </Stack>
      </MAppShell.Navbar>

      <MAppShell.Main>{children}</MAppShell.Main>
    </MAppShell>
  );
};
